/**
 * Park-and-ride amenity / address block shown under mycar legs ending at a P+R.
 */

import { IonIcon } from "@ionic/react";
import Tooltip from "@mui/material/Tooltip";
import {
    bicycleOutline,
    carOutline,
    flashOutline,
    linkOutline,
    locationOutline,
} from "ionicons/icons";
import React, { useMemo } from "react";
import MotorcycleIcon from "../../../image/icons/accessibility/MotorcycleIcon";
import WheelchairIcon from "../../../image/icons/accessibility/WheelchairIcon";
import Clock from "../../../image/icons/map/details/ClockIcon.svg?react";
import { getUIColorPalette } from "../../../theme/ColorPalette";
import { getItineraryLegColorHex } from "../../../tools/TransportModeColorsTools";
import {
    buildParkAndRideDestinationDisplay,
    type ParkAndRideAmenityCapsule,
    type ParkAndRideAmenityKind,
} from "../../../tools/parkAndRideOsmTags.tools";
import { type ItineraryLegDTO } from "../../../types/dtos/api/itinerary/itinerary.dto";

/** Capsule accent colors for available amenities. */
const AMENITY_AVAILABLE_COLORS: Readonly<Record<ParkAndRideAmenityKind, string>> = {
    "fee":        "#EF4444",
    "wheelchair": "#3B82F6",
    "car":        "#8B5CF6",
    "charging":   "#EAB308",
    "bike":       "#22C55E",
    "motorcycle": "#FF7F50",
};

/** Shared color for unavailable (barred) amenity capsules. */
const AMENITY_UNAVAILABLE_COLOR = "#EF4444";

/** Free-parking fee capsule uses green with a diagonal strike. */
const FEE_FREE_COLOR = "#22C55E";

/** Diagonal strike drawn over unavailable amenity chips (except free-parking fee). */
const AMENITY_CHIP_STRIKE_COLOR = "#DC2626";

interface ItineraryDetailsParkAndRideDestinationProps {

    /** Parent mycar/car leg whose destination is a P+R. */
    "leg": ItineraryLegDTO;

    /** When `true`, use the dark overlay palette. */
    "isDarkUi": boolean;

    /**
     * When `true`, omits the left timeline bar (provided by a parent timeline wrapper).
     */
    "hideVerticalConnector"?: boolean;
}

/**
 * Resolves the CSS color for an amenity capsule.
 *
 * @param capsule Resolved amenity capsule.
 * @returns Hex color string.
 */
const resolveAmenityColor = ( capsule: ParkAndRideAmenityCapsule ): string => {
    if ( capsule.kind === "fee" ) {
        return capsule.unavailable ? FEE_FREE_COLOR : AMENITY_AVAILABLE_COLORS.fee;
    }

    if ( capsule.unavailable ) {
        return AMENITY_UNAVAILABLE_COLOR;
    }

    return AMENITY_AVAILABLE_COLORS[capsule.kind];
};

/**
 * Builds the chip border color from its accent color.
 *
 * @param color Accent color for the amenity.
 * @returns Hex color with alpha suffix used for chip borders.
 */
const buildAmenityChipBorderColor = ( color: string ): string => `${ color }70`;

/**
 * Resolves the diagonal strike color for an unavailable amenity chip.
 *
 * @param capsule Resolved amenity capsule.
 * @returns Hex color for the strike overlay.
 */
const resolveAmenityStrikeColor = ( capsule: ParkAndRideAmenityCapsule ): string => {
    if ( capsule.kind === "fee" && capsule.unavailable ) {
        return buildAmenityChipBorderColor( FEE_FREE_COLOR );
    }

    return AMENITY_CHIP_STRIKE_COLOR;
};

/**
 * Builds a French tooltip label for an amenity chip.
 *
 * @param capsule Resolved amenity capsule.
 * @returns Localized tooltip text describing the amenity state.
 */
const resolveAmenityTooltipLabel = ( capsule: ParkAndRideAmenityCapsule ): string => {
    const countSuffix = capsule.count != null && capsule.count > 0 && !capsule.unavailable
        ? ` (${ capsule.count })`
        : "";

    switch ( capsule.kind ) {
        case "fee":
            return capsule.unavailable ? "Parking gratuit" : "Parking payant";

        case "wheelchair":
            return capsule.unavailable
                ? "Non accessible en fauteuil roulant"
                : `Places PMR${ countSuffix }`;

        case "car":
            return capsule.unavailable
                ? "Aucune place voiture"
                : `Places voiture${ countSuffix }`;

        case "charging":
            return capsule.unavailable
                ? "Aucune borne de recharge"
                : `Bornes de recharge${ countSuffix }`;

        case "motorcycle":
            return capsule.unavailable
                ? "Aucune place moto"
                : `Places moto${ countSuffix }`;

        case "bike":
            return capsule.unavailable
                ? "Aucune place vélo"
                : `Places vélo${ countSuffix }`;
    }
};

/**
 * Builds chip surface styles with enough contrast on translucent itinerary cards.
 *
 * @param color Accent color for the amenity.
 * @param isDarkUi Whether the dark overlay palette is active.
 * @returns Inline style object for the chip container.
 */
const buildAmenityChipStyle = ( color: string, isDarkUi: boolean ): React.CSSProperties => ( {
    "backgroundColor": isDarkUi ? `${ color }40` : `${ color }30`,
    "borderColor":     buildAmenityChipBorderColor( color ),
    "color":           color,
} );

/**
 * Diagonal strike from the chip bottom-left corner to the top-right corner.
 *
 * @param strikeColor Stroke color for the diagonal bar.
 * @returns Overlay SVG for barred amenity chips.
 */
const ParkAndRideAmenityChipDiagonalStrike: React.FC<{ "strikeColor": string }> = ( { strikeColor } ) => {
    return (
        <svg
            aria-hidden={true}
            className="pointer-events-none absolute inset-0 h-full w-full"
            preserveAspectRatio="none"
            viewBox="0 0 100 100"
        >
            <line
                stroke={strikeColor}
                strokeLinecap="round"
                strokeWidth="1.5"
                vectorEffect="non-scaling-stroke"
                x1="2"
                x2="98"
                y1="98"
                y2="2"
            />
        </svg>
    );
};

/**
 * Renders the leading glyph for an amenity capsule.
 *
 * @param kind Amenity kind.
 * @param color Accent color applied to the icon.
 * @returns Icon node.
 */
const renderAmenityIcon = ( kind: ParkAndRideAmenityKind, color: string ): React.ReactNode => {
    if ( kind === "fee" ) {
        return (
            <span
                className="text-[10px] font-bold leading-none"
                style={{ "color": color }}
            >
                $$
            </span>
        );
    }

    if ( kind === "motorcycle" ) {
        return <MotorcycleIcon color={color} />;
    }

    if ( kind === "wheelchair" ) {
        return <WheelchairIcon color={color} />;
    }

    const icon = kind === "car"
        ? carOutline
        : kind === "charging"
            ? flashOutline
            : bicycleOutline;

    return (
        <IonIcon
            aria-hidden={true}
            className="text-[12px]"
            icon={icon}
            style={{ "color": color }}
        />
    );
};

/**
 * Compact amenity chip for a single park-and-ride OSM attribute.
 *
 * @param props Capsule props.
 * @returns Chip element.
 */
const ParkAndRideAmenityChip: React.FC<{
    "capsule":  ParkAndRideAmenityCapsule;
    "isDarkUi": boolean;
}> = ( { capsule, isDarkUi } ) => {
    const color = resolveAmenityColor( capsule );
    const strikeColor = resolveAmenityStrikeColor( capsule );
    const tooltipLabel = resolveAmenityTooltipLabel( capsule );
    const showCount = capsule.count != null && capsule.count > 0 && !capsule.unavailable;

    return (
        <Tooltip
            arrow
            placement="top"
            slotProps={{ "popper": { "sx": { "zIndex": 2147483647 } } }}
            title={tooltipLabel}
        >
            <span
                aria-label={tooltipLabel}
                className="relative inline-flex cursor-help items-center gap-0.5 rounded border border-solid px-1 py-0.5"
                style={buildAmenityChipStyle( color, isDarkUi )}
            >
                {renderAmenityIcon( capsule.kind, color )}
                {showCount
                    ? (
                        <span className="text-[10px] font-semibold leading-none">
                            {capsule.count}
                        </span>
                    )
                    : null}
                {capsule.unavailable
                    ? <ParkAndRideAmenityChipDiagonalStrike strikeColor={strikeColor} />
                    : null}
            </span>
        </Tooltip>
    );
};

/**
 * Renders P+R amenity capsules, address, and operator website under a mycar leg.
 *
 * @param props Component props. See {@link ItineraryDetailsParkAndRideDestinationProps}.
 * @returns Destination details block, or `null` when the leg is not a P+R destination.
 */
const ItineraryDetailsParkAndRideDestination: React.FC<ItineraryDetailsParkAndRideDestinationProps> = ( {
    leg,
    isDarkUi,
    hideVerticalConnector = false,
} ) => {
    const palette = getUIColorPalette( isDarkUi );
    const display = useMemo( () => buildParkAndRideDestinationDisplay( leg.to ), [ leg.to ] );

    if ( display == null ) {
        return null;
    }

    const hasCapsules = display.capsules.length > 0;
    const hasOpeningHours = display.openingHours != null && display.openingHours.length > 0;
    const hasAddress = display.address != null && display.address.length > 0;
    const hasWebsite = display.website != null && display.website.length > 0;

    if ( !hasCapsules && !hasOpeningHours && !hasAddress && !hasWebsite ) {
        return null;
    }

    const content = (
        <div className="flex w-full min-w-0 flex-col gap-1 pb-2 text-xs">
            {hasCapsules
                ? (
                    <div className="flex max-w-full flex-wrap items-center gap-1">
                        {display.capsules.map( ( capsule ) => (
                            <ParkAndRideAmenityChip
                                capsule={capsule}
                                isDarkUi={isDarkUi}
                                key={`${ capsule.kind }-${ capsule.unavailable ? "no" : "yes" }`}
                            />
                        ) )}
                    </div>
                )
                : null}
            {hasAddress
                ? (
                    <span
                        className="flex max-w-full items-center"
                        style={{ "color": palette.overlayTextSecondary }}
                    >
                        <IonIcon
                            aria-hidden={true}
                            className="mr-1 shrink-0 text-[12px]"
                            icon={locationOutline}
                            style={{ "color": palette.overlayTextSecondary }}
                        />
                        <span className="min-w-0 truncate">
                            {display.address}
                        </span>
                    </span>
                )
                : null}
            {hasWebsite
                ? (
                    <a
                        className="inline-flex max-w-full items-center truncate font-semibold underline-offset-2 hover:underline"
                        href={display.website ?? undefined}
                        onClick={( event ) => {
                            // Keep the parent leg selection handler from firing.
                            event.stopPropagation();
                        }}
                        rel="noopener noreferrer"
                        style={{ "color": palette.overlayTextPrimary }}
                        target="_blank"
                        title={display.operator ?? display.website ?? undefined}
                    >
                        <IonIcon
                            aria-hidden={true}
                            className="mr-1 shrink-0 text-[12px]"
                            icon={linkOutline}
                            style={{ "color": palette.overlayTextPrimary }}
                        />
                        {display.operator ?? "Site web"}
                    </a>
                )
                : null}
            {hasOpeningHours
                ? (
                    <Tooltip
                        arrow
                        placement="top"
                        slotProps={{ "popper": { "sx": { "zIndex": 2147483647 } } }}
                        title={`Horaires : ${ display.openingHours ?? "" }`}
                    >
                        <span
                            aria-label={`Horaires : ${ display.openingHours ?? "" }`}
                            className="flex max-w-full cursor-help items-center text-[11px] leading-tight"
                            style={{ "color": palette.overlayTextSecondary }}
                        >
                            <Clock
                                aria-hidden={true}
                                className="mr-1 h-[11px] w-[11px] shrink-0"
                                style={{ "color": palette.overlayTextSecondary }}
                            />
                            <span className="min-w-0 truncate">
                                {display.openingHours}
                            </span>
                        </span>
                    </Tooltip>
                )
                : null}
        </div>
    );

    if ( hideVerticalConnector ) {
        return (
            <div
                className="w-full min-w-0"
                id={`itinerary-details-leg-${ leg.id }-park-and-ride`}
            >
                {content}
            </div>
        );
    }

    return (
        <div
            className="flex w-full items-stretch"
            id={`itinerary-details-leg-${ leg.id }-park-and-ride`}
        >
            <div className="flex items-stretch justify-center pl-[36px] pr-2">
                <span
                    className="mt-[-15px] block w-[3px] self-stretch"
                    style={{ "backgroundColor": getItineraryLegColorHex( leg ) }}
                />
            </div>
            {content}
        </div>
    );
};

export default ItineraryDetailsParkAndRideDestination;
